import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useProgress } from '../../hooks/useProgress';
import { supabase } from '../../lib/supabaseClient';
import { Link } from 'react-router-dom';

interface ContentCard {
  title: string;
  description: string;
  icon: string;
  path: string;
  color: string;
}

interface GradeProgressData {
  grade: string;
  displayName: string;
  totalLessons: number;
  completed: number;
}

export default function DashboardPage() {
  const { user, profile, loading, signOut } = useAuth();
  const { progress, getTotalCompleted, getGradeProgress, getLastActivity, loading: progressLoading } = useProgress();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setPasswordMessage('Password must be at least 6 characters');
      return;
    }

    setPasswordLoading(true);
    setPasswordMessage('');

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    setPasswordLoading(false);

    if (error) {
      setPasswordMessage(error.message);
    } else {
      setPasswordMessage('Password updated successfully!');
      setNewPassword('');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordMessage('');
      }, 2000);
    }
  };

  // Grade progress data with estimated lesson counts
  const gradeProgressData: GradeProgressData[] = [
    { grade: 'kindergarten', displayName: 'Kindergarten', totalLessons: 10, completed: 0 },
    { grade: 'grade-1', displayName: 'Grade 1', totalLessons: 12, completed: 0 },
    { grade: 'grade-2', displayName: 'Grade 2', totalLessons: 12, completed: 0 },
    { grade: 'grade-3', displayName: 'Grade 3', totalLessons: 12, completed: 0 },
    { grade: 'grade-4', displayName: 'Grade 4', totalLessons: 12, completed: 0 },
    { grade: 'grade-5', displayName: 'Grade 5', totalLessons: 12, completed: 0 },
    { grade: 'grade-6', displayName: 'Grade 6', totalLessons: 12, completed: 0 },
    { grade: 'grade-7', displayName: 'Grade 7', totalLessons: 12, completed: 0 },
    { grade: 'grade-8', displayName: 'Grade 8', totalLessons: 12, completed: 0 },
    { grade: 'grade-9', displayName: 'Grade 9 (Algebra 1)', totalLessons: 15, completed: 0 },
    { grade: 'grade-10', displayName: 'Grade 10 (Geometry)', totalLessons: 15, completed: 0 },
    { grade: 'grade-11', displayName: 'Grade 11 (Algebra 2)', totalLessons: 15, completed: 0 },
    { grade: 'grade-12', displayName: 'Grade 12 (Pre-Calculus)', totalLessons: 15, completed: 0 }
  ].map(grade => ({
    ...grade,
    completed: getGradeProgress(grade.grade).length
  }));

  const totalCompleted = getTotalCompleted();
  const lastActivity = getLastActivity();
  const totalLessons = gradeProgressData.reduce((sum, g) => sum + g.totalLessons, 0);
  const overallProgress = totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0;

  const contentCards: ContentCard[] = [
    {
      title: 'Grade Hub',
      description: 'Access all grade levels K-12',
      icon: 'ri-book-open-line',
      path: '/grades/hub',
      color: 'bg-teal-500'
    },
    {
      title: 'Worksheets',
      description: 'Practice with printable worksheets',
      icon: 'ri-file-list-3-line',
      path: '/worksheets',
      color: 'bg-blue-500'
    },
    {
      title: 'Free Lessons',
      description: 'Explore free sample lessons',
      icon: 'ri-lightbulb-line',
      path: '/free-lessons',
      color: 'bg-purple-500'
    },
    {
      title: 'Algebra Regents',
      description: 'NY Regents exam preparation',
      icon: 'ri-calculator-line',
      path: '/grades/algebra-regents',
      color: 'bg-orange-500'
    },
    {
      title: 'Geometry Regents',
      description: 'NY Geometry Regents prep',
      icon: 'ri-shape-line',
      path: '/grades/geometry-regents',
      color: 'bg-green-500'
    },
    {
      title: 'Success Stories',
      description: 'See student achievements',
      icon: 'ri-trophy-line',
      path: '/success-stories',
      color: 'bg-yellow-500'
    }
  ];

  if (loading || progressLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return null;
  }

  const isActive = profile.membership_status === 'ACTIVE';
  const initials = user.email
    ?.split('@')[0]
    .slice(0, 2)
    .toUpperCase() || 'ME';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {initials}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Welcome back!</h1>
                <p className="text-sm text-gray-600 mt-1">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Membership Status Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Membership Status</h2>
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                    isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {isActive ? (
                    <>
                      <i className="ri-checkbox-circle-fill mr-1"></i>
                      ACTIVE
                    </>
                  ) : (
                    <>
                      <i className="ri-information-line mr-1"></i>
                      INACTIVE
                    </>
                  )}
                </span>
              </div>
              {profile.membership_tier && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Plan:</span> {profile.membership_tier}
                </p>
              )}
              {profile.billing_cycle && (
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Billing:</span> {profile.billing_cycle}
                </p>
              )}
            </div>
            {!isActive && (
              <a
                href="/#membership-tiers"
                className="px-5 py-2.5 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors shadow-sm whitespace-nowrap"
              >
                <i className="ri-vip-crown-line mr-2"></i>
                Upgrade Now
              </a>
            )}
          </div>
          {!isActive && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <i className="ri-alert-line mr-2"></i>
                Upgrade to an active membership to unlock all grade content, worksheets, and exam prep materials.
              </p>
            </div>
          )}
        </div>

        {/* My Progress Section */}
        {isActive && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">My Progress</h2>
              {lastActivity && (
                <p className="text-sm text-gray-500">
                  Last activity: {new Date(lastActivity).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Overall Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4 border border-teal-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl">
                    <i className="ri-book-mark-line"></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-teal-900">{totalCompleted}</p>
                    <p className="text-sm text-teal-700">Lessons Completed</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white text-xl">
                    <i className="ri-percent-line"></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{overallProgress}%</p>
                    <p className="text-sm text-blue-700">Overall Progress</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-white text-xl">
                    <i className="ri-trophy-line"></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-900">{gradeProgressData.filter(g => g.completed > 0).length}</p>
                    <p className="text-sm text-purple-700">Grades Started</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress by Grade */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <i className="ri-bar-chart-box-line text-purple-600"></i>
                Progress by Grade
              </h2>
              <div className="space-y-6">
                {gradeProgressData.map((grade) => {
                  const percentage = grade.totalLessons > 0 
                    ? Math.round((grade.completed / grade.totalLessons) * 100) 
                    : 0;
                  const isCompleted = percentage === 100;

                  return (
                    <div key={grade.grade} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold">
                            {grade.grade}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800">{grade.displayName}</h3>
                            <p className="text-sm text-gray-600">
                              {grade.completed} / {grade.totalLessons} lessons
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-2xl font-bold ${isCompleted ? 'text-green-600' : 'text-gray-700'}`}>
                            {percentage}%
                          </span>
                          {isCompleted && (
                            <Link
                              to={`/certificate/${grade.grade}`}
                              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                            >
                              <i className="ri-award-line text-lg"></i>
                              View Certificate
                            </Link>
                          )}
                        </div>
                      </div>
                      <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
                            isCompleted 
                              ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                              : 'bg-gradient-to-r from-blue-500 to-purple-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      {isCompleted && (
                        <div className="mt-3 flex items-center gap-2 text-green-600 text-sm font-semibold">
                          <i className="ri-checkbox-circle-fill text-lg"></i>
                          Grade Completed!
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {totalCompleted === 0 && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <i className="ri-information-line mr-2"></i>
                  Start exploring lessons to track your progress! Visit any grade level and begin learning.
                </p>
              </div>
            )}
          </div>
        )}

        {/* My Content Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">My Content</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contentCards.map((card) => {
              const isClickable = isActive || card.path === '/free-lessons' || card.path === '/success-stories';
              
              return (
                <div
                  key={card.path}
                  onClick={() => isClickable && navigate(card.path)}
                  className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all ${
                    isClickable
                      ? 'cursor-pointer hover:shadow-md hover:border-teal-300 hover:-translate-y-1'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center text-white text-2xl mb-4`}>
                    <i className={card.icon}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{card.description}</p>
                  {isClickable ? (
                    <div className="flex items-center text-teal-600 text-sm font-medium">
                      Access Now
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  ) : (
                    <div className="flex items-center text-gray-400 text-sm font-medium">
                      <i className="ri-lock-line mr-2"></i>
                      Requires Active Membership
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Account Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Settings</h2>
          <div className="space-y-3">
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-lock-password-line mr-2"></i>
              Change Password
            </button>
          </div>
        </div>
      </main>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordMessage('');
                  setNewPassword('');
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter new password"
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>
              {passwordMessage && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm ${
                    passwordMessage.includes('success')
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {passwordMessage}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordMessage('');
                    setNewPassword('');
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {passwordLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}